// Dummy DATASET for the admin Digital receipt requests tab (sample rows only).
//
// There is no backend endpoint for this view yet, so the saga serves this data when
// the API call fails (see saga.js). Every field mirrors the shape the real
// `/user/requests/digital_receipt/` endpoint is expected to return, so wiring the real
// endpoint later is a drop-in replacement. This module holds ONLY the dummy rows +
// client-side filter/sort helpers, so it is safe to delete (along with the fallback
// branches in saga.js) once the backend exists. Production constants (PROGRAM_META)
// live in ./constants.js and must survive that deletion.
import { PROGRAM_META } from "./constants"

const BUSINESSES = [
  {
    name: "Kurata Kobe",
    website: "kuratakobe.com",
    address: "2-7-1 Sakuragi, Chuo Ward, Kobe, Hyogo 650-0021, Japan"
  },
  {
    name: "Acme Coffee",
    website: "acmecoffee.com",
    address: "1420 Lakeshore Boulevard, Suite 300, Chicago, IL 60601, USA"
  },
  { name: "Nordic Supply", website: "nordicsupply.co", address: "Storgata 14, 0155 Oslo, Norway" },
  {
    name: "Bright Labs",
    website: "brightlabs.io",
    address: "55 Hoxton Square, London N1 6PB, United Kingdom"
  },
  {
    name: "Palma Goods",
    website: "palmagoods.com",
    address: "Carrer de Sant Miquel 30, 07002 Palma, Spain"
  }
]

const REQUESTERS = [
  { name: "Nawras Ganim", email: "nawras@kuratakobe.com" },
  { name: "Maya Chen", email: "maya.chen@acmecoffee.com" },
  { name: "Ingrid Halvorsen", email: "ingrid@nordicsupply.co" },
  { name: "Tom Whitfield", email: "tom@brightlabs.io" },
  { name: "Lucia Ferrer", email: "lucia@palmagoods.com" }
]

const STATUS_CYCLE = [
  "pending",
  "enrolled",
  "unenrolled",
  "rejected",
  "enrolled",
  "enrolled",
  "pending",
  "enrolled"
]
const PROGRAM_CYCLE = [["verifi"], ["ethoca"], ["verifi", "ethoca"], ["verifi"], ["ethoca"]]

// Deterministic per-row dates, varied so the date filters have data to act on.
const isoMinusDays = (base, days) => {
  const d = new Date(base)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString()
}

const activityFor = (biz, requester, status) => [
  {
    id: "a3",
    actor: requester.name,
    type: "changed",
    event: "Changed: Company name, Support phone, Support email, Refund policy URL",
    timestamp: "2025-11-29T07:59:00Z"
  },
  {
    id: "a2",
    actor: requester.name,
    type: "pending",
    event: "Pending",
    timestamp: "2025-11-29T07:59:00Z"
  },
  {
    id: "a1",
    actor: requester.name,
    type: status,
    event: STATUS_LABELS[status] || "Enrolled",
    timestamp: "2025-11-29T07:59:00Z"
  }
]

const STATUS_LABELS = {
  pending: "Pending",
  enrolled: "Enrolled",
  unenrolled: "Unenrolled",
  rejected: "Rejected",
  needs_update: "Needs update"
}

// A pending profile-update payload (Current vs Proposed) for the update_info folder
// and for any row flagged has_pending_update.
const proposedChangesFor = (biz) => ({
  submitted_on: "2026-05-20T09:14:00Z",
  changes: [
    { field: "Company name", current: `${biz.name} LLC`, proposed: `${biz.name} Co., Ltd.` },
    { field: "Support phone", current: "+81 78 391 0220", proposed: "+81 78 391 9988" },
    { field: "Support email", current: `support@${biz.website}`, proposed: `help@${biz.website}` },
    {
      field: "Refund policy URL",
      current: `${biz.website}/refunds`,
      proposed: `${biz.website}/refund-policy`
    }
  ]
})

const buildRow = (i, { status, isUpdate }) => {
  const biz = BUSINESSES[i % BUSINESSES.length]
  const requester = REQUESTERS[i % REQUESTERS.length]
  const programs = PROGRAM_CYCLE[i % PROGRAM_CYCLE.length]
  const rowStatus = isUpdate ? "needs_update" : status
  const hasPendingUpdate = isUpdate || (rowStatus === "enrolled" && i % 4 === 0)
  const created = isoMinusDays("2026-05-28T16:32:00Z", i)
  const updated = isoMinusDays("2026-06-25T09:14:00Z", i % 12)

  return {
    id: `dr-${isUpdate ? "u" : "r"}-${i}`,
    sequence_number: 324 - i,
    status: rowStatus,
    programs,
    program: PROGRAM_META[programs[0]]?.label,
    created_timestamp: created,
    updated_timestamp: updated,
    business_account: biz.name,
    business_account_id: 1000 + i,
    user_full_name: requester.name,
    user_email: requester.email,
    // Overview detail
    requester_name: requester.name,
    requester_email: requester.email,
    merchant_id: `MID-1${String(20340 + i).slice(-5)}`,
    business_name: `${biz.name} LLC`,
    business_website: biz.website,
    company_address: biz.address,
    product_name: "Pro Plan subscription",
    product_description:
      "Monthly delivery of single-origin specialty coffee beans roasted to order, billed on the first of each month.",
    billing_descriptors: [
      `${biz.name.toUpperCase().replace(/\s/g, "")}* SUB`,
      `SQ *${biz.name.split(" ")[0].toUpperCase()}`,
      biz.website,
      `${biz.name.split(" ")[0].toUpperCase()}*RENEW`
    ],
    acquirer_bins: ["425827", "542418", "401288", "511265"],
    caids: ["1234567890", "9876543210", "4455667788"],
    support_phone: "+81 78 391 0220",
    support_email: `support@${biz.website}`,
    terms_url: `${biz.website}/terms`,
    refund_policy_url: `${biz.website}/refunds`,
    cancellation_url: `${biz.website}/cancellation`,
    logo_url: null,
    request_status: rowStatus,
    status_updated_on: updated,
    has_pending_update: hasPendingUpdate,
    profile_update: hasPendingUpdate ? proposedChangesFor(biz) : null,
    activity_log: activityFor(biz, requester, rowStatus)
  }
}

// Main "Digital receipt" list: enrollment requests across statuses. Seeded > one page
// (page_size 50) so pagination is demonstrable in the playground.
const MAIN_ROWS = Array.from({ length: 60 }, (_, i) =>
  buildRow(i, { status: STATUS_CYCLE[i % STATUS_CYCLE.length], isUpdate: false })
)

// "Update info" list: profile-update requests awaiting manual review.
const UPDATE_ROWS = Array.from({ length: 30 }, (_, i) => buildRow(i, { isUpdate: true }))

const toArray = (v) =>
  Array.isArray(v) ? v.slice() : v ? String(v).split(",").filter(Boolean) : []

const isNoneSelected = (v) =>
  !v || v === "all" || (Array.isArray(v) && (!v.length || v.includes("all")))

const applyStatusFilter = (rows, requestStatus) => {
  if (isNoneSelected(requestStatus)) return rows
  const selected = toArray(requestStatus)
  return rows.filter((r) => selected.includes(r.status))
}

const applyProgramsFilter = (rows, programs) => {
  if (isNoneSelected(programs)) return rows
  const selected = toArray(programs)
  return rows.filter((r) => (r.programs || []).some((p) => selected.includes(p)))
}

// Free-text "tags" filters (user email / full name / business account): a row matches
// if its field contains ANY entered term (case-insensitive substring).
const applyTagFilter = (rows, field, values) => {
  const terms = toArray(values)
    .map((v) => String(v).toLowerCase().trim())
    .filter(Boolean)
  if (!terms.length) return rows
  return rows.filter((r) => {
    const v = String(r[field] || "").toLowerCase()
    return terms.some((t) => v.includes(t))
  })
}

// Date-range filter: value is a [from, to] array of date strings.
const applyDateFilter = (rows, field, range) => {
  const arr = Array.isArray(range) ? range : []
  if (!arr.length) return rows
  const day = (v) => String(v).slice(0, 10)
  const from = arr[0] ? new Date(`${day(arr[0])}T00:00:00Z`).getTime() : -Infinity
  const to = arr[1] ? new Date(`${day(arr[1])}T23:59:59Z`).getTime() : Infinity
  return rows.filter((r) => {
    const t = new Date(r[field]).getTime()
    return t >= from && t <= to
  })
}

// Applies a DRF-style `ordering` string (e.g. "created_timestamp" or "-status") so the
// sortable column headers are observable against the dummy data.
const applySort = (rows, ordering) => {
  if (!ordering) return rows
  const desc = ordering.startsWith("-")
  const field = desc ? ordering.slice(1) : ordering
  const sorted = [...rows].sort((a, b) => {
    const av = a[field]
    const bv = b[field]
    if (av === bv) return 0
    if (typeof av === "number" && typeof bv === "number") return av - bv
    return String(av) < String(bv) ? -1 : 1
  })
  return desc ? sorted.reverse() : sorted
}

// Mimics a paginated DRF list response, honouring EVERY filter + sort client-side (there
// is no backend for this admin view yet). Reads the raw filters from params._filters.
export const getDummyResult = (params = {}) => {
  const { folder, page = 1, page_size = 50, ordering } = params
  const f = params._filters || params
  let rows = folder === "update_info" ? UPDATE_ROWS : MAIN_ROWS
  rows = applyStatusFilter(rows, f.request_status)
  rows = applyProgramsFilter(rows, f.programs)
  rows = applyTagFilter(rows, "user_email", f.user_email)
  rows = applyTagFilter(rows, "user_full_name", f.user_full_name)
  rows = applyTagFilter(rows, "business_account", f.business_account)
  rows = applyDateFilter(rows, "created_timestamp", f.created_timestamp)
  rows = applyDateFilter(rows, "updated_timestamp", f.updated_timestamp)
  rows = applySort(rows, ordering)
  const start = (page - 1) * page_size
  return {
    count: rows.length,
    next: null,
    previous: null,
    results: rows.slice(start, start + page_size)
  }
}
